import { Fragment } from 'react';

import type { LegendItem } from '../../../../../core/components/legend/categorical/types/legend.js';
import { ChartSeriesAction } from '../../../../../core/components/series-action/ChartSeriesAction.js';
import type { SeriesActionsTemplate } from '../../../../../core/types/series-actions-template.js';
import { safeGet } from '../../../../../core/utils/safe-get.js';
import {
  AREA_VARIANT,
  BAR_VARIANT,
  RECT_VARIANT,
  type RectSeriesWithDatapoints,
  type SeriesToMetadata,
  type XYChartSeriesWithDatapoints,
} from '../../../../types/xy-chart-internals.js';

/**
 * Filters rect series datapoints to only those matching the selected category.
 * For categorical RectSeries, each legend item represents a category.
 */
function filterRectDatapointsByCategory(
  series: RectSeriesWithDatapoints,
  categoryName: string,
) {
  return series.data.filter((datapoint) => datapoint.value === categoryName);
}

export function getXYLegendActions(
  seriesWithDatapoints: XYChartSeriesWithDatapoints[],
  seriesToMetadata: SeriesToMetadata,
) {
  return (item?: LegendItem) => {
    if (!item) {
      return;
    }

    const currentSeries = seriesWithDatapoints.find((s) => {
      if (s.type === RECT_VARIANT) {
        return item.id === `${s.id}--${item.name}`;
      }
      return s.id === item.id;
    });

    if (currentSeries) {
      const metadata = safeGet(seriesToMetadata, currentSeries);

      const datapoints =
        currentSeries.type === RECT_VARIANT
          ? filterRectDatapointsByCategory(currentSeries, item.name)
          : currentSeries.data;

      const isAreaOrBarSeries =
        currentSeries.type === AREA_VARIANT ||
        currentSeries.type === BAR_VARIANT;

      const firstDatapoint = datapoints[0];

      const datapointElement = isAreaOrBarSeries
        ? { datapoint: firstDatapoint, name: item.name }
        : firstDatapoint;

      const seriesActions: SeriesActionsTemplate = metadata.actions?.(
        datapointElement,
        { datapoints, name: item.name },
      );

      if (seriesActions) {
        if (
          seriesActions instanceof Promise ||
          seriesActions.type === ChartSeriesAction ||
          seriesActions.type === Fragment
        ) {
          return seriesActions;
        } else {
          throw new Error('Invalid type passed for getXYLegendActions!');
        }
      }
    }
  };
}
